import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Mail, MailOpen, Briefcase, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return <p className="text-[var(--cv-text-dim)] text-sm">Loading analytics...</p>;
  if (!data) return <p className="text-[var(--cv-text-dim)] text-sm">Unable to load analytics.</p>;

  const maxCount = Math.max(...(data.contacts_by_week?.map((w) => w.count) || [1]), 1);

  return (
    <div data-testid="admin-analytics-panel">
      <h2
        className="text-lg font-bold uppercase tracking-wider mb-6"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        Dashboard
      </h2>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="analytics-stats">
        <StatCard icon={<Mail size={16} />} label="Total Contacts" value={data.contacts_total} testId="stat-contacts-total" />
        <StatCard icon={<MailOpen size={16} />} label="Unread" value={data.contacts_unread} testId="stat-contacts-unread" />
        <StatCard icon={<Briefcase size={16} />} label="Ventures" value={data.ventures_total} testId="stat-ventures" />
        <StatCard icon={<FileText size={16} />} label="Posts Published" value={`${data.posts_published}/${data.posts_total}`} testId="stat-posts" />
      </div>

      {/* Weekly chart */}
      <div className="mb-4 flex items-center gap-2">
        <BarChart3 size={14} className="text-[var(--cv-text-dim)]" />
        <span
          className="text-xs uppercase tracking-[0.15em] text-[var(--cv-text-dim)]"
          style={{ fontFamily: 'var(--font-mono)' }}
        >
          Contacts per Week
        </span>
      </div>

      {data.contacts_by_week?.length > 0 ? (
        <div className="flex items-end gap-1.5 h-[140px]" data-testid="analytics-chart">
          {data.contacts_by_week.map((w, i) => {
            const height = Math.max((w.count / maxCount) * 100, 4);
            return (
              <motion.div
                key={w.week}
                className="flex-1 flex flex-col items-center gap-1"
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: 1, scaleY: 1 }}
                transition={{ duration: 0.5, delay: 0.04 * i, ease: [0.16, 1, 0.3, 1] }}
                style={{ originY: 1 }}
              >
                <span className="text-[0.55rem] text-[var(--cv-text-dim)]" style={{ fontFamily: 'var(--font-mono)' }}>
                  {w.count}
                </span>
                <div
                  className="w-full bg-white/15 hover:bg-white/30 transition-colors rounded-sm"
                  style={{ height: `${height}%` }}
                  title={`${w.week}: ${w.count} contacts`}
                />
                <span
                  className="text-[0.5rem] text-[var(--cv-text-dim)] whitespace-nowrap"
                  style={{ fontFamily: 'var(--font-mono)' }}
                >
                  {w.week.split('-W')[1] ? `W${w.week.split('-W')[1]}` : w.week}
                </span>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <p className="text-[var(--cv-text-dim)] text-sm">No data yet.</p>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div
      className="border border-[var(--cv-border)] p-4 flex flex-col gap-2"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[var(--cv-text-dim)]">
        {icon}
        <span className="text-[0.6rem] uppercase tracking-[0.15em]" style={{ fontFamily: 'var(--font-mono)' }}>
          {label}
        </span>
      </div>
      <span className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
        {value}
      </span>
    </div>
  );
}
